import React, { useEffect, useState, useRef } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import VehicleServiceService from "../services/vehicleServiceService";
import StorageService from "../services/StorageService";
import Breadcrumb from "../components/Breadcrumb";
import Loading from "../components/loading";
import ReactPaginate from "react-paginate";
import XLSX from "xlsx-js-style";
import usePermission from "../utils/UsePermission";
import ScreenData from "../config/ScreenData";
import utility from "../utils/utility";
import MessageDialog from "../components/MessageDialog";


export default function ViewVehicleServices() {
  const [services, setServices] = useState([]);
  const [selected, setSelected] = useState(null); // 🔹 RADIO (single select)
  const [loading, setLoading] = useState(true);
  const [searchText, setSearchText] = useState("");
  const [message, setMessage] = useState("");
  const [msgType, setMsgType] = useState("error");
  const [pageCount, setPageCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const itemsPerPage = 20;
  // const messageTimer = useRef(null);
  const navigate = useNavigate();
  const location = useLocation();

  const fetchVehicleServices = async () => {
    setLoading(true);
    try {
      const companyId = StorageService.activeCompany().companyId;
      const search = searchText.trim().toLowerCase();
      // const res = await VehicleServiceService.getByCompanyId(companyId);
      // const data = res?.data || res || [];
      // setServices(data.sort((a, b) => b.id - a.id));



      // const data = res?.data?.data || [];
      // setServices(data);

      // setPageCount(res?.data?.totalPages || 0);
      const res = search
        ? await VehicleServiceService.exportVehicleService(companyId)
        : await VehicleServiceService.getByCompanyId(
          companyId,
          currentPage,
          itemsPerPage,
          searchText
        );

      let data = search ? res?.data || [] : res?.data?.data || [];

      if (search) {
        data = data.filter(
          (s) =>
            s.vehicleType?.toLowerCase().includes(search) ||
            s.vehicleName?.toLowerCase().includes(search) ||
            s.registrationNo?.toLowerCase().includes(search)
        );
      }

      setServices(search ? data.slice((currentPage - 1) * itemsPerPage, currentPage * itemsPerPage) : data);

      setPageCount(search ? Math.ceil(data.length / itemsPerPage) : res?.data?.totalPages || 0);
    } catch (err) {
      console.error("Error fetching vehicle services:", err);
      showMessage("Failed to load vehicle service data", "error");
    } finally {
      setLoading(false);
    }
  };



  // Refresh after add/edit
  useEffect(() => {
    if (location.state?.refresh) {
      fetchVehicleServices();
      navigate(location.pathname, { replace: true });
    }
  }, [location]);

  const exportExcel = async () => {
    if (!can("canView")) {
      showMessage("No permission", "error");
      return;
    }
    try {
      const company = StorageService.activeCompany();

      const res = await VehicleServiceService.exportVehicleService(
        company.companyId
      );

      if (!res?.status || !res.data.length) {
        showMessage("No data to export", "error");
        return;
      }

      const services = res.data;

      const wb = XLSX.utils.book_new();
      const ws = {};
      ws["!merges"] = [];

      let row = 0;

      // ✅ STYLES
      const thin = { style: "thin" };
      const thick = { style: "medium" };

      const center = {
        horizontal: "center",
        vertical: "center",
        wrapText: true,
      };

      // =========================
      // TITLE
      // =========================
      XLSX.utils.sheet_add_aoa(ws, [["Vehicle Service Report"]], {
        origin: { r: row, c: 1 },
      });

      ws["!merges"].push({
        s: { r: row, c: 1 },
        e: { r: row, c: 10 },
      });

      const titleCell = XLSX.utils.encode_cell({ r: row, c: 1 });

      ws[titleCell].s = {
        font: { bold: true, sz: 16 },
        alignment: center,
      };

      row += 2;

      // =========================
      // HEADER
      // =========================
      const header = [
        "Company",
        "Vehicle Type",
        "Vehicle",
        "Registration No",
        "Service Date",
        "Next Service Date",
        "Service KMS",
        "Next Service KMS",
        "Status",
      ];

      const startRow = row; // ✅ START TRACK

      XLSX.utils.sheet_add_aoa(ws, [header], {
        origin: { r: row, c: 1 },
      });

      header.forEach((_, i) => {
        const cell = XLSX.utils.encode_cell({ r: row, c: i + 1 });

        ws[cell].s = {
          font: { bold: true },
          alignment: center,
        };
      });

      row++;

      // =========================
      // DATA
      // =========================
      services.forEach((v) => {
        const rowData = [
          v.companyName,
          v.vehicleType,
          v.vehicleName,
          v.registrationNo,
          v.serviceDate
            ? utility.formatDateDDMMMYYYY(v.serviceDate)
            : "-",

          v.nextServiceDate
            ? utility.formatDateDDMMMYYYY(v.nextServiceDate)
            : "-",
          v.serviceKMS,
          v.nextServiceKMS,
          v.status,
        ];

        XLSX.utils.sheet_add_aoa(ws, [rowData], {
          origin: { r: row, c: 1 },
        });

        row++;
      });

      const endRow = row - 1; // ✅ END TRACK

      // =========================
      // APPLY BORDERS 🔥
      // =========================
      const colCount = header.length;

      for (let r = startRow; r <= endRow; r++) {
        for (let c = 1; c <= colCount; c++) {
          const cell = XLSX.utils.encode_cell({ r, c });

          if (!ws[cell]) {
            ws[cell] = { t: "s", v: "" };
          }

          ws[cell].s = ws[cell].s || {};
          ws[cell].s.alignment = center;

          ws[cell].s.border = {
            top: r === startRow ? thick : thin,
            bottom: r === endRow ? thick : thin,
            left: c === 1 ? thick : thin,
            right: c === colCount ? thick : thin,
          };
        }
      }

      // =========================
      // COLUMN WIDTH
      // =========================
      ws["!cols"] = [
        { wch: 20 },
        { wch: 18 },
        { wch: 20 },
        { wch: 18 },
        { wch: 15 },
        { wch: 18 },
        { wch: 15 },
        { wch: 18 },
        { wch: 12 },
      ];

      // =========================
      // EXPORT
      // =========================
      XLSX.utils.book_append_sheet(wb, ws, "VehicleService");

      XLSX.writeFile(wb, "VehicleService.xlsx");
    } catch (error) {
      console.error(error);
      showMessage("Error exporting data", "error");
    }
  };

  const showMessage = (text, type = "error") => {
    setMessage(text);
    setMsgType(type);
    // if (messageTimer.current) clearTimeout(messageTimer.current);
    // messageTimer.current = setTimeout(() => setMessage(""), 4000);
  };

  const { permission, checkView, can } = usePermission(
    ScreenData.Vehicle_Service,
    showMessage
  );

  useEffect(() => {
    if (permission) {
      if (checkView()) {
        fetchVehicleServices();
      }
    }
  }, [currentPage, permission, searchText]);

  useEffect(() => {
    setCurrentPage(1);
  }, [searchText]);

  const handleAdd = () => {
    if (!can("canAdd")) {
      showMessage("No permission", "error");
      return;
    }
    navigate("/cpnl/vehicleservice/add");
  };

  const handlePageClick = (event) => {
    const selectedPage = event.selected + 1;
    setCurrentPage(selectedPage);
  };

  const handleModify = () => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    if (!selected) {
      showMessage("Please select one Vehicle Service to modify.");
      return;
    }
    navigate(`/cpnl/vehicleservice/add/${selected}`);
  };



  const handleToggleStatus = async (item) => {
    if (!can("canModify")) {
      showMessage("No permission", "error");
      return;
    }
    const newStatus = item.status === "Active" ? "Inactive" : "Active";

    if (!window.confirm(`Are you sure you want to change status to ${newStatus}?`))
      return;

    setLoading(true);
    try {
      await VehicleServiceService.updateStatus(item.id);
      showMessage("Status updated successfully", "success");
      fetchVehicleServices();
    } catch (err) {
      console.error(err);
      showMessage("Error updating status", "error");
    } finally {
      setLoading(false);
    }
  };

  // 🔹 RADIO SELECT
  const handleSelect = (id) => {
    setSelected(id);
  };

  // 🔍 FRONTEND SEARCH FILTER
  const filteredServices = services;

  return (
    <>
      <Breadcrumb pageTitle="Vehicle Service" secondTitle="Vehicle Management" />

      {/* {message && (
        <div
          className={`alert ${
            msgType === "success" ? "alert-success" : "alert-danger"
          } d-flex justify-content-between align-items-center mb-2 p-2`}
        >
          <span>{message}</span>
          <button
            type="button"
            className="btn-close"
            onClick={() => setMessage("")}
          ></button>
        </div>
      )} */}


      <MessageDialog
        message={message}
        type={msgType}
        setMessage={setMessage}
        setMsgType={setMsgType}
      />


      <div className="row">
        <div className="card py-2 bg-white shadow-sm">
          {/* <div className="mb-3 pb-2 border-bottom">
            <button
              className="d-inline-flex btn btn-success me-2"
              onClick={handleAdd}
              disabled={loading}
            >
              <i className="ti ti-plus me-1"></i> Add
            </button>

            <button
              className="d-inline-flex btn btn-warning me-2"
              onClick={handleModify}
              disabled={loading}
            >
              <i className="ti ti-pencil me-1"></i> Modify
            </button>
          </div> */}

          <div className="mb-3 pb-2 border-bottom d-flex align-items-end gap-3">

            {/* 🔍 SEARCH FIELD */}
            <div>
              <input
                type="text"
                className="form-control"
                placeholder="Search Vehicle Service"
                value={searchText}
                onChange={(e) => setSearchText(e.target.value)}
              />
            </div>


            <div className="ms-auto d-flex gap-2">

              {can("canAdd") && (
                <button
                  className="d-inline-flex btn btn-success"
                  onClick={handleAdd}
                  disabled={loading}
                >
                  <i className="ti ti-plus me-1"></i> Add
                </button>
              )}

              {can("canModify") && (
                <button
                  className="d-inline-flex btn btn-warning"
                  onClick={handleModify}
                  disabled={loading}
                >
                  <i className="ti ti-pencil me-1"></i> Modify
                </button>
              )}

              {can("canView") && (
                <button
                  className="btn btn-success"
                  onClick={exportExcel}
                >
                  <i className="ti ti-download me-1"></i>
                  Export Excel
                </button>
              )}

            </div>


          </div>

          <div className="table-responsive">
            <table className="table table-striped table-bordered mb-0">
              <thead className="table-light">
                <tr>
                  <th>Select</th>
                  <th>Vehicle Type</th>
                  <th>Vehicle</th>
                  <th>Service Date</th>
                  <th>Next Service Date</th>
                  <th>Service KMS/Hours</th>
                  <th>Next Service KMS/Hours</th>
                  <th>Status</th>
                </tr>
              </thead>

              <tbody>
                {loading ? (
                  <tr>
                    <td colSpan="7" className="text-center py-4">
                      <Loading />
                    </td>
                  </tr>
                ) : filteredServices.length === 0 ? (
                  <tr>
                    <td colSpan="8" className="text-center text-muted">
                      No vehicle services found
                    </td>
                  </tr>
                ) : (
                  filteredServices.map((s) => {
                    const isActive =
                      s.status === "Active" ||
                      s.status === 1 ||
                      s.status === true;

                    return (
                      <tr key={s.id}>
                        <td>
                          <input
                            type="radio"
                            name="vehicleServiceSelect"
                            checked={selected === s.id}
                            onChange={() => handleSelect(s.id)}
                          />
                        </td>
                        <td>{s.vehicleType || s.vehicleTypeId || "-"}</td>
                        <td>{s.vehicleName || s.vehicleId || "-"}-{s.registrationNo || "-"}</td>
                        <td>{utility.formatDateDDMMMYYYY(s.serviceDate)}</td>
                        <td>{utility.formatDateDDMMMYYYY(s.nextServiceDate)}</td>
                        <td>{s.serviceKMS}</td>
                        <td>{s.nextServiceKMS}</td>
                        <td>
                          <a
                            className={`badge ${isActive ? "bg-success" : "bg-secondary"
                              }`}
                            style={{ cursor: "pointer" }}
                            onClick={() => handleToggleStatus(s)}
                          >
                            {isActive ? "Active" : "Inactive"}
                          </a>
                        </td>
                      </tr>
                    );
                  })
                )}
              </tbody>
            </table>

            <ReactPaginate
              breakLabel="..."
              nextLabel="Next >"
              onPageChange={handlePageClick}
              pageRangeDisplayed={3}
              marginPagesDisplayed={2}
              pageCount={pageCount}
              previousLabel="< Previous"
              containerClassName="pagination-container"
              pageClassName="page-item"
              pageLinkClassName="page-link"
              previousClassName="page-item"
              previousLinkClassName="page-link"
              nextClassName="page-item"
              nextLinkClassName="page-link"
              activeClassName="active"
            />


          </div>

        </div>
      </div>
    </>
  );
}
